import React, { useState, useEffect, useMemo } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { SbModal, SbButton, SbNotification, SbImageCropUpload, SbSpin } from '../../../../uiCore';
import { getJdBanner, uploadJdBanner } from '../../../../Api/apiService';

/**
 * BannerModal — view and replace the organization's JD banner.
 * On open it loads the current banner so it stays visible; the edit icon in the
 * top-right corner opens the file explorer to pick a replacement.
 * The backend stores the image in GCS and rewrites the banner URL in all of the
 * org's templates so future JDs use it (existing JDs are unaffected).
 */
const BannerModal = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [bannerUrl, setBannerUrl] = useState(null); // currently saved banner
  const [bannerFile, setBannerFile] = useState(null); // newly picked, not yet saved

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    setBannerFile(null);
    setFetching(true);
    getJdBanner()
      .then((data) => {
        if (!cancelled) setBannerUrl(data?.url || null);
      })
      .catch(() => {
        // Non-blocking: the admin can still upload a banner.
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const previewUrl = useMemo(
    () => (bannerFile ? URL.createObjectURL(bannerFile) : bannerUrl),
    [bannerFile, bannerUrl],
  );

  useEffect(() => {
    if (!bannerFile) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [bannerFile, previewUrl]);

  const handleSave = async () => {
    if (!bannerFile) {
      SbNotification.info('No Image', 'Please select a banner image first');
      return;
    }
    setLoading(true);
    try {
      const data = await uploadJdBanner(bannerFile);
      if (data?.url) setBannerUrl(data.url);
      setBannerFile(null);
      SbNotification.success('Banner Updated', 'New JDs will use the updated banner');
      onClose();
    } catch (error) {
      SbNotification.error('Upload Failed', error?.response?.data?.message || 'Could not update the JD banner');
    } finally {
      setLoading(false);
    }
  };

  const renderUploadArea = (url) =>
    url ? (
      <div
        style={{
          position: 'relative',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <img src={url} alt="JD banner" style={{ display: 'block', width: '100%', height: 'auto' }} />
        <SbButton
          aria-label="Change banner"
          title="Change banner"
          buttonType="secondary"
          icon={<EditOutlined />}
          style={{ position: 'absolute', top: '8px', right: '8px' }}
        />
      </div>
    ) : (
      <div
        style={{
          border: '2px dashed #d1d5db',
          borderRadius: '8px',
          padding: '32px 16px',
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
        <p style={{ fontSize: '14px', fontWeight: 500, margin: '8px 0 4px 0' }}>Upload JD banner</p>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>PNG or JPG · max 2MB · 5.5:1.5 banner</span>
      </div>
    );

  return (
    <SbModal
      title="JD Banner"
      open={visible}
      onCancel={onClose}
      width={480}
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={onClose}>
          Cancel
        </SbButton>,
        <SbButton key="save" buttonType="primary" loading={loading} disabled={!bannerFile} onClick={handleSave}>
          Save
        </SbButton>,
      ]}
    >
      {fetching ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <SbSpin />
        </div>
      ) : (
        <SbImageCropUpload
          value={previewUrl}
          onChange={setBannerFile}
          accept=".png,.jpg,.jpeg"
          maxSize={2}
          aspectRatio={5.5}
          renderUploadArea={renderUploadArea}
        />
      )}
    </SbModal>
  );
};

export default BannerModal;
